import { Link } from 'react-router-dom';
import { Leaf, Hand, HeartHandshake, Sparkles } from 'lucide-react';
import { SEO } from '@/components/SEO';

const VALUES = [
  {
    icon: Leaf,
    title: 'All Natural',
    description: 'Powerful African botanicals, free from parabens and synthetic fragrance.',
  },
  {
    icon: Hand,
    title: 'Handmade',
    description: 'Formulated and bottled by hand in South Africa since 2011.',
  },
  {
    icon: HeartHandshake,
    title: 'Family-owned',
    description: 'A family business built on trust, transparency and real results.',
  },
  {
    icon: Sparkles,
    title: 'AI-supported',
    description: 'Leroy pairs tradition with technology to personalise every routine.',
  },
];

export function About() {
  return (
    <div style={{ background: '#FAF7F2' }} className="min-h-screen">
      <SEO
        title="About Marple & Co | Handmade African Botanical Skincare Since 2011"
        description="Founded in 2011 in Modderfontein, Edenvale, South Africa. Marple & Co handcrafts premium skincare, wellness teas and aromatherapy candles using indigenous African botanicals. Family-owned."
        path="/about"
        schema={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "About Marple & Co",
          "url": "https://marplecompany.co.za/about",
          "description": "Founded in 2011 in Modderfontein, Edenvale. Handmade African botanical skincare.",
          "mainEntity": { "@id": "https://marplecompany.co.za/#organization" }
        }}
      />

      {/* Hero */}
      <section
        className="text-center"
        style={{
          background: 'linear-gradient(135deg, #567D38, #1A2E12)',
          padding: '80px 28px',
        }}
      >
        <img
          src="/brand/mark.png"
          alt="Marple & Co"
          style={{ width: 60, opacity: 0.9, marginBottom: 18, filter: 'brightness(0) invert(1)' }}
          className="mx-auto"
        />
        <h1 className="font-elegant" style={{ fontSize: 48, color: '#fff', margin: 0 }}>
          Our Story
        </h1>
        <p
          className="font-elegant italic"
          style={{ color: '#d4ec5c', fontSize: 18, marginTop: 8 }}
        >
          Handmade · Rejuvenating · All Natural · Est. 2011
        </p>
      </section>

      {/* Mission statement */}
      <section style={{ padding: '56px 28px', maxWidth: 760, margin: '0 auto' }}>
        <p
          className="font-elegant text-center"
          style={{ fontSize: 22, lineHeight: 1.7, color: '#1A2E12', margin: 0 }}
        >
          Marple &amp; Company began in a home kitchen in 2011, blending Southern-African
          botanicals into skincare that actually worked for sensitive, sun-exposed skin. Today we
          pair that handmade tradition with an AI advisor, Leroy, so every routine is rooted in
          your skin — not someone else's.
        </p>
      </section>

      {/* Value props */}
      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
        style={{ maxWidth: 1180, margin: '0 auto', padding: '0 28px 64px' }}
      >
        {VALUES.map(({ icon: Icon, title, description }) => (
          <div
            key={title}
            className="bg-white text-center"
            style={{
              borderRadius: 16,
              padding: 24,
              border: '1px solid rgba(217,179,140,.4)',
            }}
          >
            <div
              className="flex items-center justify-center mx-auto"
              style={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                background: 'rgba(189,214,48,.15)',
                marginBottom: 14,
              }}
            >
              <Icon className="w-6 h-6" style={{ color: '#567D38' }} />
            </div>
            <div
              className="font-display font-semibold uppercase tracking-wide"
              style={{ fontSize: 14, color: '#1A2E12', marginBottom: 6 }}
            >
              {title}
            </div>
            <p style={{ fontSize: 13, color: '#6B7B6E', lineHeight: 1.6, margin: 0 }}>
              {description}
            </p>
          </div>
        ))}
      </section>

      {/* Closing CTA */}
      <section
        className="text-center"
        style={{ background: '#F7F5EE', padding: '56px 28px' }}
      >
        <span
          className="font-display font-semibold uppercase tracking-widest block mb-3"
          style={{ fontSize: 13, color: '#567D38' }}
        >
          Begin
        </span>
        <h2 className="font-elegant" style={{ fontSize: 32, color: '#1A2E12', margin: '0 0 24px' }}>
          Ready to meet your skin?
        </h2>
        <Link
          to="/skin-analysis"
          className="inline-flex items-center gap-2 font-bold rounded-full transition-colors hover:bg-[#97c23c]"
          style={{
            background: '#BDD630',
            color: '#1A2E12',
            padding: '14px 32px',
            fontSize: 14,
          }}
        >
          Start your free analysis →
        </Link>
      </section>
    </div>
  );
}
